import { useState, useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { SupabaseClient } from "@supabase/supabase-js";
import { v4 as uuidv4 } from "uuid";
import type { DocumentCategory } from "@/domains/documents/services/documentService";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { extractFileMetadata } from "@/utils/fileMetadata";
import { getContainerType } from "@/domains/documents/constants/fileTypes";
import { runVersionItemsInOrder } from "@/domains/documents/utils/runVersionItemsInOrder";
import {
  liveVersionSpan,
  stagedItemName,
  type StagedVersionItem,
} from "@/domains/documents/types/addVersions";
import {
  BuildingStorageQuotaError,
  buildingStorageQuotaKey,
  type BuildingStorageQuota,
} from "@/domains/documents/services/buildingStorageQuota";
import { uploadsBlocked } from "@/domains/documents/utils/storagePresentation";
import { reportStorageAdmissionFailure } from "@/domains/documents/utils/storageBatchFeedback";
import { collectFolderPaths } from "@/domains/documents/utils/uploadSelection";
import { RESUMABLE_THRESHOLD_BYTES } from "@/domains/documents/constants/upload";
import { useUploadQueueStore } from "@/hooks/useUploadQueueStore";
import { reaction } from "mobx";
import type { QueuedFileInstance } from "@/stores/uploadQueue/models";
import { computeFileHash } from "@/utils/fileHash";
import { ensureFreshToken, uploadFileWithProgress } from "@/stores/uploadQueue/repository";
import { mirrorToBackups } from "@/stores/uploadQueue/backupMirror";
import {
  addDocumentAsVersion,
  checkUploadCollisions,
  isAddAsVersionFailure,
  fetchFilenamesInFolder,
  type AddAsVersionResult,
} from "@/stores/documents/repository";
import { nextDuplicateSuffix } from "@/utils/duplicateSuffix";
import { billingGuardMessage } from "@/domains/billing/messages";
import { track } from "@/lib/posthog";
import { fileExtProp, sizeBucket } from "@/lib/analytics/eventProps";
import { capture } from "@/lib/sentry/capture";
import {
  cancelBuildingStorageUpload,
  invalidateBuildingStorageQuota,
  parseBuildingStorageId,
  reserveBuildingStorageUploads,
  storageAdmissionErrorFrom,
  storageQuotaErrorMessage,
} from "@/domains/documents/services/buildingStorageQuota";
import { discardBuildingStorageUpload } from "@/domains/documents/services/discardBuildingStorageUpload";
import { notifyPinnedDocuments } from "@/domains/documents/services/pinnedDocuments";
import type {
  CollisionDecision,
  CollisionPair,
} from "@/domains/documents/components/organisms/UploadCollisionDialog";

// Type for the versioning RPC response
interface VersioningRpcResponse {
  action: "created" | "versioned" | "skipped" | "resurrected";
  reason?: string;
  document_id: number;
  version: number;
  version_group: string;
  file_path?: string;
}

/**
 * Type guard to validate RPC response structure at runtime.
 * Prevents runtime errors if backend API changes or returns unexpected data.
 */
function isVersioningAction(value: unknown): value is VersioningRpcResponse["action"] {
  return (
    typeof value === "string" && ["created", "versioned", "skipped", "resurrected"].includes(value)
  );
}

function isPositiveSafeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value > 0;
}

function isVersioningRpcResponse(data: unknown): data is VersioningRpcResponse {
  if (typeof data !== "object" || data === null) return false;
  const obj = data as Record<string, unknown>;
  return (
    isVersioningAction(obj.action) &&
    isPositiveSafeInteger(obj.document_id) &&
    isPositiveSafeInteger(obj.version) &&
    typeof obj.version_group === "string"
  );
}

const UNCONFIRMED_REGISTRATION_MESSAGE =
  "Document registration could not be confirmed. Check the project before retrying; the upload was retained.";

function isTransactionalRpcRejection(error: { code?: string }): boolean {
  return storageAdmissionErrorFrom(error) !== null;
}

/** The existing document a new version is being uploaded onto. */
export interface VersionUploadTarget {
  id: string | number;
  filename: string;
  version_group: string | null;
  project_id: number | null;
  folder_id: number | null;
  parent_document_id: string | number | null;
  container_type?: string | null;
  file_path?: string | null;
}

interface UseDocumentUploadProps {
  buildingId: string;
  onUploadComplete?: () => void;
  onDataExtracted?: (data: any) => void;
  initialCategory?: DocumentCategory;
  projectId?: string;
  requireProject?: boolean;
  /**
   * Extra path segments inserted between `{orgId}/{buildingId}` and the
   * uniquely-generated filename in the Supabase Storage object key.
   * Used by issue-attached uploads to get a path like
   * `{org}/{building}/issues/{issue_id}/{uuid}.{ext}`.
   */
  storagePathSegments?: string[];
  /**
   * Fires once a document row exists (from the create_document_with_versioning
   * RPC), regardless of whether it was newly created or resurrected. Used by
   * issue-attached uploads to insert into pm_issue_documents.
   */
  onDocumentCreated?: (documentId: number) => Promise<void> | void;
  /**
   * Fires when the upload was skipped because the same file already exists at
   * that path, with the existing document's id. Callers that attach the upload
   * to something (a chat message) use it; nothing new was created.
   */
  onExistingDocument?: (documentId: number) => Promise<void> | void;
  /**
   * Folder names to get-or-create under the project root, for callers that
   * know their destination folder (e.g. every PM issue attachment lands in the
   * project's system "Issue Attachments" folder).
   *
   * Prefer this over encoding the folder in `relativePath`. `relativePath` is
   * meant for browser-supplied paths and is validated as a whole, so a folder
   * smuggled in as `"Issue Attachments/<filename>"` is silently discarded —
   * along with the folder — whenever the FILENAME contains one of the
   * characters `validateRelativePath` rejects (`< > : " | ? *`). The file then
   * lands at the project root with no error shown.
   */
  folderPathSegments?: string[];
  /**
   * Analytics only — which surface the upload came from. Set per mounting
   * component, so a component that both drops and opens a modal reports one
   * value for both; splitting those needs the source to ride on each file.
   */
  analyticsSource?: "drag" | "modal" | "chat";
}

export type UploadCallOptions = Pick<
  UseDocumentUploadProps,
  "onDocumentCreated" | "onExistingDocument"
> & {
  /**
   * Cancels the upload up to the point the document row is created. Once that
   * request has been issued it is not interrupted: the row may already be
   * committed, and the completion callbacks are the only way to hear of it.
   * An aborted upload shows no error toast — the caller that aborted it says why.
   */
  signal?: AbortSignal;
};

/** Characters no folder name may contain — filesystem-hostile plus control chars. */
// eslint-disable-next-line no-control-regex
const ILLEGAL_FOLDER_NAME_CHARS = /[<>:"|?*\x00-\x1f]/;

/**
 * Guards a single folder name on its way to `get_or_create_folder`. Every
 * folder we create passes through here, whichever source named it — a caller's
 * `folderPathSegments` or a browser relative path — so a separator, a traversal
 * segment or a control character can never become a folder name. Throws rather
 * than dropping the folder: the upload then fails visibly instead of quietly
 * filing at the project root.
 */
function assertSafeFolderName(folderName: string): void {
  if (
    !folderName.trim() ||
    folderName.includes("/") ||
    folderName.includes("\\") ||
    folderName.includes("..") ||
    ILLEGAL_FOLDER_NAME_CHARS.test(folderName)
  ) {
    throw new Error(`Invalid folder name: "${folderName}"`);
  }
}

/**
 * Validates a relative path to prevent path traversal attacks.
 * Returns the validated path or null if the path is invalid.
 *
 * @param relativePath - The path to validate (typically from webkitRelativePath)
 * @returns The validated path, or null if invalid
 */
function validateRelativePath(relativePath: string | null | undefined): string | null {
  if (!relativePath) return null;

  // Check for path traversal attempts
  if (
    relativePath.includes("..") ||
    relativePath.startsWith("/") ||
    relativePath.startsWith("\\") ||
    relativePath.includes("\\")
  ) {
    console.warn("[useDocumentUpload] Invalid path detected (traversal attempt):", relativePath);
    return null;
  }

  // Split and validate each folder name
  const parts = relativePath.split("/");
  for (const part of parts) {
    // Skip empty parts (shouldn't happen in valid paths)
    if (!part) continue;

    // Check for illegal characters that could cause issues on filesystems
    if (ILLEGAL_FOLDER_NAME_CHARS.test(part)) {
      console.warn("[useDocumentUpload] Invalid path detected (illegal chars):", relativePath);
      return null;
    }
  }

  return relativePath;
}

/** SQLSTATE for `lock_timeout`: `get_or_create_folder` waited 10s for the folder-path lock. */
const FOLDER_LOCK_TIMEOUT = "55P03";

/**
 * `acquire_folder_path_lock` lets the native lock_timeout error propagate, so
 * PostgREST reports 55P03. The message check is a backstop in case a later
 * migration wraps it and re-raises under another code.
 */
function isFolderLockTimeout(error: { code?: string; message?: string }): boolean {
  return (
    error.code === FOLDER_LOCK_TIMEOUT ||
    /canceling statement due to lock timeout/i.test(error.message ?? "")
  );
}
/** Waits before each retry of a lock-timed-out `get_or_create_folder`. */
const FOLDER_LOCK_RETRY_DELAYS_MS = [1000, 3000];
/**
 * Lock-timeout retries spent so far, shared by every folder level of one
 * destination — so a deep hierarchy waits as long as a single folder at worst,
 * not once per level.
 */
type FolderLockRetryBudget = { used: number };

/** Resolves after `ms`, or rejects with the signal's reason as soon as it aborts. */
function abortableDelay(ms: number, signal?: AbortSignal): Promise<void> {
  const abortError = () => signal?.reason ?? new DOMException("Aborted", "AbortError");
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * One folder of an upload's destination, as an id: the live folder of that
 * name under `parentId` when there is one, else a new one.
 *
 * `get_or_create_folder` takes the ONE folder-path advisory lock the whole
 * database shares (every document_folders write in every org queues on it,
 * under a 10s lock_timeout) before it even looks — so an attachment to the
 * per-project "Issue Attachments" folder, which almost always exists already,
 * failed with "canceling statement due to lock timeout" whenever any other
 * folder write anywhere held the lock. The read below is the RPC's own probe
 * (same predicate, same `ORDER BY id LIMIT 1`, same RLS — the RPC is SECURITY
 * INVOKER). It mirrors `public.get_or_create_folder` as defined in
 * franklin-data-pipeline migration `20260824133041_folder_probe_error_attribution`;
 * a migration that changes that lookup must change `findLiveFolderId` with it. So an existing folder resolves with no lock at all, and the lock
 * is only taken when a folder really has to be created. A failed probe is not
 * an answer; the RPC still decides.
 *
 * Creation retries a lock timeout: it is transient by design (the SharePoint
 * sync retries it the same way), and a retry of get-or-create is idempotent.
 * The retries come out of `retryBudget`, which the caller shares across levels
 * (and across paths, for a bulk upload's folder tree).
 */
async function getOrCreateFolder(
  supabase: SupabaseClient,
  {
    name,
    parentId,
    orgId,
    buildingId,
    projectId,
    userId,
    signal,
    retryBudget = { used: 0 },
  }: {
    name: string;
    parentId: number | null;
    orgId: string;
    buildingId: number;
    projectId: number | null;
    userId: number;
    signal?: AbortSignal;
    retryBudget?: FolderLockRetryBudget;
  }
): Promise<number> {
  for (;;) {
    // Before every attempt, not just the first: whoever held the lock through a
    // timeout has often created this very folder by the time the wait is over.
    const existingId = await findLiveFolderId(supabase, {
      name,
      parentId,
      orgId,
      buildingId,
      projectId,
    });
    if (existingId !== null) return existingId;

    // A cancelled upload must not go on to create a folder it will never fill.
    signal?.throwIfAborted();
    const { data, error } = await supabase.rpc("get_or_create_folder", {
      p_name: name,
      p_parent_id: parentId,
      p_org_id: orgId,
      p_building_id: buildingId,
      p_project_id: projectId,
      p_created_by: userId,
    });

    if (error) {
      if (isFolderLockTimeout(error) && retryBudget.used < FOLDER_LOCK_RETRY_DELAYS_MS.length) {
        await abortableDelay(FOLDER_LOCK_RETRY_DELAYS_MS[retryBudget.used++], signal);
        continue;
      }
      throw new Error(`Failed to create folder "${name}": ${error.message}`);
    }

    if (!data || data.length === 0) {
      throw new Error(`Failed to create folder "${name}": No data returned`);
    }

    // Database function returns array with single row
    return data[0].id;
  }
}

/**
 * `get_or_create_folder`'s existence probe, without its lock. Null = not found,
 * not answerable, or not a folder this shortcut may hand out.
 *
 * The row is the one the RPC would pick (its predicate, its `ORDER BY id`), and
 * it is accepted only if it is also a visible custom project folder hanging off
 * no document — what the RPC itself creates (it inserts `is_custom` and
 * `is_project_scoped` true, with or without a project). Anything else (a hidden,
 * SharePoint-synced, system or document-attached folder holding the key) is left
 * to the RPC to decide, so a hit is always the RPC's own answer and never a wider
 * one. The flags are checked on the RPC's pick rather than filtered in the
 * query: filtering first could pick a different row than the RPC would.
 */
async function findLiveFolderId(
  supabase: SupabaseClient,
  {
    name,
    parentId,
    orgId,
    buildingId,
    projectId,
  }: {
    name: string;
    parentId: number | null;
    orgId: string;
    buildingId: number;
    projectId: number | null;
  }
): Promise<number | null> {
  try {
    let query = supabase
      .from("document_folders")
      .select("id, hidden, is_custom, is_project_scoped, parent_document_id")
      .eq("org_id", orgId)
      .eq("building_id", buildingId)
      .eq("name", name)
      .eq("is_deleted", false);
    query = parentId === null ? query.is("parent_id", null) : query.eq("parent_id", parentId);
    query = projectId === null ? query.is("project_id", null) : query.eq("project_id", projectId);
    const { data, error } = await query.order("id").limit(1);
    if (error) throw new Error(error.message);
    if (!data || data.length === 0) return null;
    const row = data[0] as {
      id: unknown;
      hidden?: boolean | null;
      is_custom?: boolean | null;
      is_project_scoped?: boolean | null;
      parent_document_id?: number | null;
    };
    if (typeof row.id !== "number") throw new Error(`Unexpected folder id: ${String(row.id)}`);
    if (
      row.hidden !== false ||
      row.is_custom !== true ||
      row.is_project_scoped !== true ||
      row.parent_document_id !== null
    ) {
      return null;
    }
    return row.id;
  } catch (error) {
    // Not found is an answer; this is not. It still falls back to the RPC, so
    // report it — a probe that always fails would put every upload back on the lock.
    capture(error instanceof Error ? error : new Error(String(error)), {
      tags: { feature: "document-upload", stage: "folder-probe" },
      fingerprint: ["document-upload", "folder-probe"],
      context: { folder: { name, parentId, buildingId, projectId } },
    });
    return null;
  }
}

async function createFolderHierarchy({
  supabase,
  orgId,
  buildingId,
  projectId,
  folderNames,
  userId,
  signal,
}: {
  supabase: SupabaseClient;
  orgId: string;
  buildingId: number;
  projectId: number | null;
  folderNames: string[];
  userId: number;
  signal?: AbortSignal;
}): Promise<number | null> {
  if (folderNames.length === 0) {
    return null;
  }

  let parentId: number | null = null;
  const retryBudget: FolderLockRetryBudget = { used: 0 };

  for (const folderName of folderNames) {
    assertSafeFolderName(folderName);

    parentId = await getOrCreateFolder(supabase, {
      name: folderName,
      parentId,
      orgId,
      buildingId,
      projectId,
      userId,
      signal,
      retryBudget,
    });
  }

  return parentId;
}

/**
 * Creates a folder hierarchy starting from an optional parent folder.
 * Used when uploading files into a selected folder with nested folder structure.
 *
 * @param parentFolderId - The starting parent folder (user's selected folder)
 * @returns The deepest folder ID in the hierarchy
 */
async function createFolderHierarchyWithParent({
  supabase,
  orgId,
  buildingId,
  projectId,
  folderNames,
  userId,
  parentFolderId,
  retryBudget = { used: 0 },
}: {
  supabase: SupabaseClient;
  orgId: string;
  buildingId: number;
  projectId: number | null;
  folderNames: string[];
  userId: number;
  parentFolderId: number | null;
  /** Shared by a caller that walks several paths, so the whole walk has one budget. */
  retryBudget?: FolderLockRetryBudget;
}): Promise<number | null> {
  // If no folder names to create, return the parent folder ID
  if (folderNames.length === 0) {
    return parentFolderId;
  }

  // Start from the parent folder (may be null for root)
  let currentParentId: number | null = parentFolderId;

  for (const folderName of folderNames) {
    assertSafeFolderName(folderName);

    currentParentId = await getOrCreateFolder(supabase, {
      name: folderName,
      parentId: currentParentId,
      orgId,
      buildingId,
      projectId,
      userId,
      retryBudget,
    });
  }

  return currentParentId;
}

/**
 * Creates the whole folder tree a bulk upload implies, parents first, BEFORE
 * any file is queued: the folders are in the Folders view the moment the upload
 * starts rather than one by one as files finish, and folders holding no files —
 * which no file's path would ever create — exist at all.
 *
 * A path `validateRelativePath` rejects is skipped, not fatal: that is what the
 * per-file path does with the same path (its files land in the parent folder),
 * and every descendant of a rejected folder is rejected with it. Record creation
 * still resolves each file's folder through the same get-or-create RPC, so this
 * is idempotent with it, and a folder removed mid-upload comes back rather than
 * stranding the files meant for it.
 */
async function createUploadFolderTree({
  supabase,
  orgId,
  buildingId,
  projectId,
  userId,
  parentFolderId,
  folderPaths,
}: {
  supabase: SupabaseClient;
  orgId: string;
  buildingId: number;
  projectId: number | null;
  userId: number;
  parentFolderId: number | null;
  folderPaths: string[];
}): Promise<void> {
  const idByPath = new Map<string, number | null>();
  const retryBudget: FolderLockRetryBudget = { used: 0 };
  for (const path of folderPaths) {
    if (!validateRelativePath(path)) continue;
    const slash = path.lastIndexOf("/");
    const parentPath = slash === -1 ? null : path.slice(0, slash);
    const parentId = parentPath === null ? parentFolderId : idByPath.get(parentPath);
    // Parents come first, so a missing parent was itself rejected above.
    if (parentId === undefined) continue;
    idByPath.set(
      path,
      await createFolderHierarchyWithParent({
        supabase,
        orgId,
        buildingId,
        projectId,
        folderNames: [path.slice(slash + 1)],
        userId,
        parentFolderId: parentId,
        retryBudget,
      })
    );
  }
}

export function useDocumentUpload({
  buildingId,
  onUploadComplete,
  onDataExtracted,
  initialCategory = "documents",
  projectId,
  requireProject = false,
  storagePathSegments,
  onDocumentCreated,
  onExistingDocument,
  folderPathSegments,
  analyticsSource = "modal",
}: UseDocumentUploadProps) {
  const { toast } = useToast();
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [activeUploads, setActiveUploads] = useState(0);
  const [fileProgresses, setFileProgresses] = useState<
    {
      id: string;
      name: string;
      progress: number;
      status: "uploading" | "processing" | "complete" | "failed";
    }[]
  >([]);

  const isUploading = activeUploads > 0;
  const { userProfile } = useAuth();
  const { currentOrganization } = useOrganizationContext();

  // Collision-resolution dialog state. `resolve` is set while a bulk upload
  // is awaiting the user's decisions; `cancel` aborts the whole batch.
  const [collisionDialog, setCollisionDialog] = useState<{
    pairs: CollisionPair[];
    resolve: (decisions: Record<string, CollisionDecision>) => void;
    cancel: () => void;
  } | null>(null);

  const handleFileUpload = async (
    file: File,
    relativePath?: string,
    projectIdOverride?: string,
    /**
     * Replace the hook's completion callbacks for this call only — for a
     * caller that must know which call a document came from — and optionally
     * make the call cancellable.
     */
    callOptions?: UploadCallOptions
  ) => {
    const documentCreated = callOptions?.onDocumentCreated ?? onDocumentCreated;
    const existingDocument = callOptions?.onExistingDocument ?? onExistingDocument;
    const signal = callOptions?.signal;
    if (!file) {
      console.error("No file provided for upload");
      return;
    }

    // Use override if provided, otherwise fall back to hook's projectId
    const effectiveProjectId = projectIdOverride ?? projectId;

    // Check if project is required
    if (requireProject && !effectiveProjectId) {
      toast({
        variant: "destructive",
        title: "Project Required",
        description: "Please select a project before uploading files.",
      });
      return;
    }

    const progressId = uuidv4();
    setFileProgresses((prev) => [
      ...prev,
      { id: progressId, name: file.name, progress: 0, status: "uploading" },
    ]);
    setActiveUploads((u) => u + 1);
    let reservationId: string | null = null;
    let uploadedPath: string | null = null;
    let registrationMayHaveCommitted = false;
    const numericBuildingId = parseBuildingStorageId(buildingId);

    try {
      if (!userProfile?.id) {
        throw new Error("User profile not available. Please ensure you are logged in.");
      }

      if (!currentOrganization?.id) {
        throw new Error(
          "No organisation selected. Please select an organisation before uploading files."
        );
      }

      if (numericBuildingId === null) {
        throw new Error(`Invalid building ID: ${buildingId}`);
      }

      setFileProgresses((prev) =>
        prev.map((f) => (f.id === progressId ? { ...f, status: "uploading", progress: 20 } : f))
      );

      const fileExt = file.name.split(".").pop();
      const uniqueFileName = `${uuidv4()}.${fileExt}`;
      // Standard path: {org_id}/{building_id}/{filename}.
      // Callers (e.g. issue uploads) can inject extra path segments to slot
      // files into a logical sub-tree without changing folder_id semantics.
      const extraSegments =
        storagePathSegments && storagePathSegments.length > 0
          ? `${storagePathSegments.join("/")}/`
          : "";
      const filePath = `${currentOrganization.id}/${numericBuildingId}/${extraSegments}${uniqueFileName}`;

      [{ reservationId }] = await reserveBuildingStorageUploads(numericBuildingId, [
        {
          idempotencyKey: uuidv4(),
          path: filePath,
          size: file.size,
        },
      ]);

      if (file.size > RESUMABLE_THRESHOLD_BYTES) {
        // TUS resumable: chunked + survives flaky networks. Required for
        // the 150 MB–1 GB+ IFCs production architects upload (WHA-3535).
        // The shared uploader handles the SUPABASE_OVERRIDE branch switcher +
        // apikey header (so preview branches hit the right project) and never
        // hangs on a swallowed rejection (WHA-3688).
        const result = await uploadFileWithProgress(
          file,
          filePath,
          "documents",
          (loaded, total) => {
            // Map upload progress into the 0–50 region of the file's overall
            // progress bar (50–100 covers downstream steps).
            const fraction = total > 0 ? loaded / total : 0;
            setFileProgresses((prev) =>
              prev.map((f) =>
                f.id === progressId ? { ...f, progress: Math.round(fraction * 50) } : f
              )
            );
          }
        );
        if (!result.success) {
          console.error("Resumable upload error:", result.error);
          throw new Error(`Storage upload failed: ${result.error ?? "unknown error"}`);
        }
      } else {
        const { error: uploadError } = await supabase.storage
          .from("documents")
          .upload(filePath, file);

        if (uploadError) {
          console.error("Storage upload error:", uploadError);
          throw new Error(
            storageQuotaErrorMessage(uploadError.message) ??
              `Storage upload failed: ${uploadError.message}`
          );
        }
      }
      uploadedPath = filePath;

      setFileProgresses((prev) =>
        prev.map((f) => (f.id === progressId ? { ...f, progress: 50 } : f))
      );

      // Compute content hash for versioning
      let contentHash: string | null = null;
      try {
        contentHash = await computeFileHash(file);
      } catch (hashError) {
        console.warn("[useDocumentUpload] Failed to compute file hash:", hashError);
        // Continue without hash - versioning will use path-based detection only
      }

      setFileProgresses((prev) =>
        prev.map((f) => (f.id === progressId ? { ...f, progress: 60 } : f))
      );

      // Extract embedded file metadata (EXIF, PDF info, Office XML dates)
      const fileMetadata = await extractFileMetadata(file);

      // Determine which project to use - project selection is required
      let targetProjectId: number | null = null;

      if (effectiveProjectId) {
        // Use the provided project ID, convert to number if it's a string
        targetProjectId = parseInt(effectiveProjectId, 10);
      } else {
        throw new Error("Project selection is required for document upload");
      }

      let targetFolderId: number | null = null;

      // Validate relativePath to prevent path traversal attacks
      const validatedPath = validateRelativePath(relativePath);

      // An explicit destination (folderPathSegments) wins over a browser
      // relative path: the caller named the folder, so it must not be subject
      // to filename validation the way a relativePath's segments are.
      const folderNames =
        folderPathSegments && folderPathSegments.length > 0
          ? folderPathSegments
          : validatedPath && validatedPath.includes("/")
            ? validatedPath.split("/").slice(0, -1)
            : [];

      console.log("[useDocumentUpload] Processing file:", {
        filename: file.name,
        relativePath,
        validatedPath,
        folderNames,
      });

      if (folderNames.length > 0) {
        console.log("[useDocumentUpload] Creating folder hierarchy:", {
          relativePath,
          folderNames,
          orgId: currentOrganization.id,
          buildingId: numericBuildingId,
          projectId: targetProjectId,
        });

        try {
          targetFolderId = await createFolderHierarchy({
            supabase,
            orgId: currentOrganization.id,
            buildingId: numericBuildingId,
            projectId: targetProjectId,
            folderNames,
            userId: userProfile.id,
            signal,
          });
          console.log(
            "[useDocumentUpload] Folder hierarchy created, targetFolderId:",
            targetFolderId
          );
        } catch (folderError) {
          if (signal?.aborted) throw folderError;
          console.error("Folder creation error:", folderError);
          throw new Error(
            `Failed to create folder structure: ${folderError instanceof Error ? folderError.message : "Unknown error"}`
          );
        }
      }

      // Detect special file types BEFORE INSERT to set correct state from the
      // start — DWG_CONTAINER / IFC_CONTAINER / CONTAINER route into their
      // specialised pipelines (shared helper, WHA-3689).
      const containerType = getContainerType(file.name, file.type);

      // Last point at which a cancelled upload leaves no document behind. The
      // stored object stays, as a skipped duplicate's does; no row points at it.
      signal?.throwIfAborted();

      // Use versioning RPC for atomic document creation with duplicate detection
      registrationMayHaveCommitted = true;
      const { data: rpcResult, error: rpcError } = await supabase.rpc(
        "create_document_with_versioning",
        {
          p_org_id: currentOrganization.id,
          p_building_id: numericBuildingId,
          p_project_id: targetProjectId,
          p_folder_id: targetFolderId,
          p_filename: file.name,
          p_file_path: filePath,
          p_content_type: file.type,
          p_size: file.size,
          p_content_hash: contentHash,
          p_created_by: userProfile.id,
          p_source: "direct_upload",
          p_parent_document_id: null,
          p_container_type: containerType,
        }
      );

      if (rpcError) {
        if (isTransactionalRpcRejection(rpcError)) registrationMayHaveCommitted = false;
        console.error("Document versioning RPC error:", rpcError);
        throw new Error(
          storageQuotaErrorMessage(rpcError.code) ??
            storageQuotaErrorMessage(rpcError.message) ??
            `Document creation failed: ${rpcError.message}`
        );
      }

      // Validate RPC response structure at runtime
      if (!isVersioningRpcResponse(rpcResult)) {
        console.error("Invalid RPC response format:", rpcResult);
        throw new Error("Document creation failed: Invalid response format from server");
      }

      const versionResult = rpcResult;
      if (versionResult.action === "skipped" || versionResult.action === "resurrected") {
        const completedReservationId = reservationId;
        if (completedReservationId) {
          await discardBuildingStorageUpload(filePath, completedReservationId, numericBuildingId);
        }
      }
      reservationId = null;
      invalidateBuildingStorageQuota(buildingId);

      // Placed before the skipped/resurrected early returns so every file that
      // reaches the RPC reports its outcome exactly once. The bulk path's
      // pre-flight decides "skipped" for byte-identical collisions before the
      // RPC ever sees them and emits that outcome itself; a file the USER chose
      // to skip in the collision dialog is a withdrawn upload, not an outcome,
      // and is deliberately silent.
      track("document_uploaded", {
        file_ext: fileExtProp(file.name),
        size_bucket: sizeBucket(file.size),
        action: versionResult.action,
        source: analyticsSource,
      });

      // Handle skipped duplicates using the existing document row.
      if (versionResult.action === "skipped") {
        setFileProgresses((prev) =>
          prev.map((f) => (f.id === progressId ? { ...f, progress: 100, status: "complete" } : f))
        );
        setTimeout(
          () => setFileProgresses((prev) => prev.filter((f) => f.id !== progressId)),
          3000
        );
        setActiveUploads((u) => Math.max(0, u - 1));

        if (existingDocument) {
          await existingDocument(versionResult.document_id);
        }

        // Skip the rest of the upload flow for duplicates
        return;
      }

      // Handle resurrected documents - soft-deleted doc with same content was restored
      if (versionResult.action === "resurrected") {
        setFileProgresses((prev) =>
          prev.map((f) => (f.id === progressId ? { ...f, progress: 100, status: "complete" } : f))
        );
        setTimeout(
          () => setFileProgresses((prev) => prev.filter((f) => f.id !== progressId)),
          3000
        );
        setActiveUploads((u) => Math.max(0, u - 1));

        // Invalidate queries to show the restored document
        queryClient.invalidateQueries({ queryKey: ["custom-folders", buildingId, projectId] });

        if (documentCreated) {
          await documentCreated(versionResult.document_id);
        }

        if (onUploadComplete) {
          onUploadComplete();
        }
        return;
      }

      // Build updates for fields not handled by RPC
      const postRpcUpdates: Record<string, unknown> = {};

      // For ZIP files, set state to CONTAINER (RPC defaults to UNPROCESSED)
      // DWG files: RPC sets DWG_CONTAINER via container_type parameter — no override needed
      if (containerType === "zip") {
        postRpcUpdates.state = "CONTAINER";
      }

      // Set dates from embedded file metadata (EXIF, PDF info, Office XML)
      if (fileMetadata.original_created_at) {
        postRpcUpdates.original_created_at = fileMetadata.original_created_at;
      }
      if (fileMetadata.document_content_modified_date) {
        postRpcUpdates.document_content_modified_date = fileMetadata.document_content_modified_date;
      }

      // Apply post-RPC updates if any
      if (Object.keys(postRpcUpdates).length > 0) {
        const { error: updateError } = await supabase
          .from("documents")
          .update(postRpcUpdates)
          .eq("id", versionResult.document_id);

        if (updateError) {
          console.error("[useDocumentUpload] Post-RPC update failed:", updateError);
          // Rollback: delete the created document to avoid inconsistent state
          const { error: deleteError } = await supabase
            .from("documents")
            .delete()
            .eq("id", versionResult.document_id);
          if (deleteError) {
            console.error("[useDocumentUpload] Rollback delete also failed:", deleteError);
          }
          throw new Error(`Document creation failed: ${updateError.message}`);
        }
      }

      // Second copy in the write-once backups bucket — last, once a row is known
      // to own this object for good. A backup can never be deleted, so it must
      // come after the rollback above (which deletes the row it just created)
      // and after the `skipped`/`resurrected` returns, both of which leave the
      // object we uploaded owned by nothing.
      await mirrorToBackups(filePath);

      // Create docData object for downstream compatibility
      const docData = {
        id: versionResult.document_id,
        version: versionResult.version,
        version_group: versionResult.version_group,
      };

      setFileProgresses((prev) =>
        prev.map((f) => (f.id === progressId ? { ...f, progress: 90 } : f))
      );

      // Invalidate folder-related queries (documents handled by realtime subscription)
      queryClient.invalidateQueries({ queryKey: ["custom-folders", buildingId, projectId] });

      // Documents are created with state='UNPROCESSED'
      // The orchestrator will automatically classify and extract data

      setFileProgresses((prev) =>
        prev.map((f) => (f.id === progressId ? { ...f, progress: 100, status: "complete" } : f))
      );

      setTimeout(() => setFileProgresses((prev) => prev.filter((f) => f.id !== progressId)), 3000);

      if (documentCreated) {
        await documentCreated(versionResult.document_id);
      }

      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (error) {
      if (reservationId) {
        if (uploadedPath && registrationMayHaveCommitted) {
          capture(error instanceof Error ? error : new Error(String(error)), {
            tags: { feature: "building-storage-quota", stage: "registration-unconfirmed" },
            fingerprint: ["building-storage-quota", "registration-unconfirmed"],
            context: { quota: { buildingId: numericBuildingId, path: uploadedPath } },
          });
        } else if (uploadedPath && numericBuildingId !== null) {
          await discardBuildingStorageUpload(uploadedPath, reservationId, numericBuildingId);
        } else if (numericBuildingId !== null) {
          try {
            await cancelBuildingStorageUpload(reservationId, numericBuildingId);
          } catch (cancelError) {
            capture(cancelError instanceof Error ? cancelError : new Error(String(cancelError)), {
              tags: { feature: "building-storage-quota", stage: "cancel-upload" },
              fingerprint: ["building-storage-quota", "cancel-upload"],
              context: { quota: { buildingId: numericBuildingId } },
            });
          }
        }
      }
      console.error("Document upload error details:", {
        error: error instanceof Error ? error.message : error,
        fileName: file.name,
        userProfile: userProfile?.id,
        organization: currentOrganization?.id,
        buildingId,
        stack: error instanceof Error ? error.stack : undefined,
      });

      if (!signal?.aborted) {
        toast({
          variant: "destructive",
          title: t.common.error,
          description:
            reservationId && registrationMayHaveCommitted
              ? UNCONFIRMED_REGISTRATION_MESSAGE
              : (billingGuardMessage(error, t) ??
                (error instanceof Error ? error.message : t.documents.upload.uploadError)),
        });
      }

      setFileProgresses((prev) =>
        prev.map((f) => (f.id === progressId ? { ...f, progress: 100, status: "failed" } : f))
      );
      setTimeout(() => setFileProgresses((prev) => prev.filter((f) => f.id !== progressId)), 3000);
    } finally {
      setActiveUploads((u) => Math.max(0, u - 1));
    }
  };

  /**
   * Upload a local file as the next version of an existing document,
   * regardless of filename match. Uses `p_force_version_group` in the
   * versioning RPC to bypass the (project_id, folder_id, parent_document_id,
   * filename) identity check.
   *
   * Caller is responsible for any pre-upload confirmation (e.g. warning the
   * user about a filename mismatch).
   *
   * `options.silent` suppresses the per-file toasts, cache invalidation and
   * `onUploadComplete` callback so a batch caller can emit one summary instead
   * of N. Failures still return `null`; the caller decides how to report them.
   */
  const uploadAsNewVersion = useCallback(
    async (
      file: File,
      targetDocument: VersionUploadTarget,
      projectIdOverride?: string,
      options?: { silent?: boolean }
    ): Promise<{ documentId: number; version: number; versionGroup: string } | null> => {
      const silent = options?.silent ?? false;
      if (!targetDocument.version_group) {
        toast({
          variant: "destructive",
          title: t.common.error,
          description: t.documents.versioning.addNewVersion.errorNoVersionGroup,
        });
        return null;
      }

      if (!userProfile?.id) {
        toast({
          variant: "destructive",
          title: t.common.error,
          description: "User profile not available.",
        });
        return null;
      }
      if (!currentOrganization?.id) {
        toast({
          variant: "destructive",
          title: t.common.error,
          description: "No organisation selected.",
        });
        return null;
      }

      const effectiveProjectId = projectIdOverride ?? projectId;
      const resolvedProjectId = effectiveProjectId
        ? parseInt(effectiveProjectId, 10)
        : targetDocument.project_id;

      if (
        resolvedProjectId === null ||
        resolvedProjectId === undefined ||
        isNaN(resolvedProjectId)
      ) {
        toast({
          variant: "destructive",
          title: t.common.error,
          description: t.documents.versioning.addNewVersion.errorNoProject,
        });
        return null;
      }

      const resolvedFolderId = targetDocument.folder_id;
      const resolvedParentDocId =
        targetDocument.parent_document_id !== null &&
        targetDocument.parent_document_id !== undefined
          ? typeof targetDocument.parent_document_id === "string"
            ? parseInt(targetDocument.parent_document_id, 10)
            : targetDocument.parent_document_id
          : null;

      const numericBuildingId = parseBuildingStorageId(buildingId);
      if (numericBuildingId === null) {
        toast({
          variant: "destructive",
          title: t.common.error,
          description: `Invalid building ID: ${buildingId}`,
        });
        return null;
      }

      const progressId = uuidv4();
      setFileProgresses((prev) => [
        ...prev,
        { id: progressId, name: file.name, progress: 0, status: "uploading" },
      ]);
      setActiveUploads((u) => u + 1);
      let reservationId: string | null = null;
      let uploadedPath: string | null = null;
      let registrationMayHaveCommitted = false;

      try {
        const fileExt = file.name.split(".").pop();
        const uniqueFileName = `${uuidv4()}.${fileExt}`;
        const filePath = `${currentOrganization.id}/${numericBuildingId}/${uniqueFileName}`;

        [{ reservationId }] = await reserveBuildingStorageUploads(numericBuildingId, [
          {
            idempotencyKey: uuidv4(),
            path: filePath,
            size: file.size,
          },
        ]);

        const { error: uploadError } = await supabase.storage
          .from("documents")
          .upload(filePath, file);
        if (uploadError) {
          throw new Error(
            storageQuotaErrorMessage(uploadError.message) ??
              `Storage upload failed: ${uploadError.message}`
          );
        }
        uploadedPath = filePath;

        setFileProgresses((prev) =>
          prev.map((f) => (f.id === progressId ? { ...f, progress: 50 } : f))
        );

        let contentHash: string | null = null;
        try {
          contentHash = await computeFileHash(file);
        } catch (hashError) {
          console.warn("[useDocumentUpload] Failed to compute file hash:", hashError);
        }

        setFileProgresses((prev) =>
          prev.map((f) => (f.id === progressId ? { ...f, progress: 70 } : f))
        );

        const fileMetadata = await extractFileMetadata(file);

        // Container type — DWG/IFC/zip route into their pipelines; otherwise
        // preserve the existing document's container_type so a re-upload keeps
        // its routing (shared helper, WHA-3689).
        const containerType = getContainerType(
          file.name,
          file.type,
          targetDocument.container_type ?? null
        );

        registrationMayHaveCommitted = true;
        const { data: rpcResult, error: rpcError } = await supabase.rpc(
          "create_document_with_versioning",
          {
            p_org_id: currentOrganization.id,
            p_building_id: numericBuildingId,
            p_project_id: resolvedProjectId,
            p_folder_id: resolvedFolderId,
            p_filename: file.name,
            p_file_path: filePath,
            p_content_type: file.type,
            p_size: file.size,
            p_content_hash: contentHash,
            p_created_by: userProfile.id,
            p_source: "direct_upload",
            p_parent_document_id: resolvedParentDocId,
            p_container_type: containerType,
            p_force_version_group: targetDocument.version_group,
          }
        );

        if (rpcError) {
          if (isTransactionalRpcRejection(rpcError)) registrationMayHaveCommitted = false;
          throw new Error(
            storageQuotaErrorMessage(rpcError.code) ??
              storageQuotaErrorMessage(rpcError.message) ??
              `Document creation failed: ${rpcError.message}`
          );
        }
        if (!isVersioningRpcResponse(rpcResult)) {
          throw new Error("Document creation failed: Invalid response format from server");
        }

        const versionResult = rpcResult;
        if (versionResult.action !== "versioned") {
          throw new Error("Document creation failed: Unexpected versioning result");
        }
        reservationId = null;
        invalidateBuildingStorageQuota(buildingId);

        track("document_uploaded", {
          file_ext: fileExtProp(file.name),
          size_bucket: sizeBucket(file.size),
          action: versionResult.action,
          source: analyticsSource,
        });

        // Second copy in the write-once backups bucket, now that the version
        // row exists. A new version is a new uuid path, so it gets its own
        // immutable backup; p_force_version_group always inserts a row at this
        // path — that branch returns before the RPC's skip/resurrect checks, so
        // `versioned` is its only answer.
        await mirrorToBackups(filePath);

        // Post-RPC state + metadata updates.
        const postRpcUpdates: Record<string, unknown> = {};
        if (containerType === "zip") postRpcUpdates.state = "CONTAINER";
        if (fileMetadata.original_created_at) {
          postRpcUpdates.original_created_at = fileMetadata.original_created_at;
        }
        if (fileMetadata.document_content_modified_date) {
          postRpcUpdates.document_content_modified_date =
            fileMetadata.document_content_modified_date;
        }
        if (Object.keys(postRpcUpdates).length > 0) {
          const { error: updateError } = await supabase
            .from("documents")
            .update(postRpcUpdates)
            .eq("id", versionResult.document_id);
          if (updateError) {
            console.error("[useDocumentUpload] Post-RPC update failed:", updateError);
          }
        }

        setFileProgresses((prev) =>
          prev.map((f) => (f.id === progressId ? { ...f, progress: 100, status: "complete" } : f))
        );
        setTimeout(
          () => setFileProgresses((prev) => prev.filter((f) => f.id !== progressId)),
          3000
        );

        if (!silent) {
          queryClient.invalidateQueries({ queryKey: ["custom-folders", buildingId, projectId] });

          toast({
            title: t.documents.versioning.addNewVersion.toastSuccessTitle,
            description: t.documents.versioning.addNewVersion.toastSuccessDescription
              .replace("{filename}", file.name)
              .replace("{version}", String(versionResult.version)),
          });

          if (onUploadComplete) onUploadComplete();
        }

        return {
          documentId: versionResult.document_id,
          version: versionResult.version,
          versionGroup: versionResult.version_group,
        };
      } catch (error) {
        if (reservationId) {
          if (uploadedPath && registrationMayHaveCommitted) {
            capture(error instanceof Error ? error : new Error(String(error)), {
              tags: { feature: "building-storage-quota", stage: "registration-unconfirmed" },
              fingerprint: ["building-storage-quota", "registration-unconfirmed"],
              context: { quota: { buildingId: numericBuildingId, path: uploadedPath } },
            });
          } else if (uploadedPath) {
            await discardBuildingStorageUpload(uploadedPath, reservationId, numericBuildingId);
          } else {
            try {
              await cancelBuildingStorageUpload(reservationId, numericBuildingId);
            } catch (cancelError) {
              capture(cancelError instanceof Error ? cancelError : new Error(String(cancelError)), {
                tags: { feature: "building-storage-quota", stage: "cancel-upload" },
                fingerprint: ["building-storage-quota", "cancel-upload"],
                context: { quota: { buildingId: numericBuildingId } },
              });
            }
          }
        }
        console.error("[useDocumentUpload] uploadAsNewVersion failed:", error);
        toast({
          variant: "destructive",
          title: t.common.error,
          description:
            reservationId && registrationMayHaveCommitted
              ? UNCONFIRMED_REGISTRATION_MESSAGE
              : error instanceof Error
                ? error.message
                : t.documents.upload.uploadError,
        });
        setFileProgresses((prev) =>
          prev.map((f) => (f.id === progressId ? { ...f, progress: 100, status: "failed" } : f))
        );
        setTimeout(
          () => setFileProgresses((prev) => prev.filter((f) => f.id !== progressId)),
          3000
        );
        return null;
      } finally {
        setActiveUploads((u) => Math.max(0, u - 1));
      }
    },
    [
      buildingId,
      projectId,
      userProfile,
      currentOrganization,
      queryClient,
      toast,
      t,
      onUploadComplete,
      analyticsSource,
    ]
  );

  /**
   * Commit a mixed, user-ordered list — uploaded files and existing documents
   * moved or copied in — as consecutive new versions of one document.
   *
   * Items land in list order: the first becomes the target's next version, the
   * last its current version. That ordering is why each item is awaited before
   * the next is issued — both RPCs number from `max(version) + 1` under the same
   * advisory lock, so concurrent calls would be safe but numbered arbitrarily.
   *
   * The same `targetDocument` is reused every iteration; both RPCs re-read the
   * lineage (and its current head) each call, so there is nothing to re-target.
   *
   * On the first failure the run stops rather than pushing the remaining items
   * onto a version chain the user no longer understands. What committed stays
   * committed and is reported; `committed` tells the dialog how many leading
   * items to drop so the rest stay staged for a retry.
   */
  const addVersionsInOrder = useCallback(
    async (
      items: StagedVersionItem[],
      targetDocument: VersionUploadTarget,
      projectIdOverride?: string
    ): Promise<{
      committed: number;
      firstVersion: number | null;
      lastVersion: number | null;
      touchedVersionGroups: string[];
    }> => {
      const copy = t.documents.versioning.addNewVersion;
      const results: AddAsVersionResult[] = [];
      let liveAdded = 0;

      const { ranges, failedItem } = await runVersionItemsInOrder(items, async (item) => {
        if (item.kind === "upload") {
          const uploaded = await uploadAsNewVersion(item.file, targetDocument, projectIdOverride, {
            silent: true,
          });
          if (!uploaded) return null;
          liveAdded += 1;
          return { firstVersion: uploaded.version, lastVersion: uploaded.version };
        }

        const outcome = await addDocumentAsVersion({
          targetDocumentId: Number(targetDocument.id),
          sourceDocumentId: Number(item.document.id),
          mode: item.mode,
        });
        if (isAddAsVersionFailure(outcome)) {
          if (outcome.pinned) {
            notifyPinnedDocuments([outcome.pinned]);
            return null;
          }
          toast({
            variant: "destructive",
            title: t.common.error,
            description:
              outcome.reason === "storageQuota" ? outcome.message : copy.errors[outcome.reason],
          });
          return null;
        }
        results.push(outcome.result);
        liveAdded += liveVersionSpan(item);
        return {
          firstVersion: outcome.result.firstVersion,
          lastVersion: outcome.result.lastVersion,
        };
      });

      const committed = ranges.length;
      const firstVersion = ranges[0]?.firstVersion ?? null;
      const lastVersion = ranges[committed - 1]?.lastVersion ?? null;

      if (failedItem && committed > 0) {
        // The failing step has already said why. Add the batch-level fact it
        // cannot know: what survived, and that the rest is still listed.
        toast({
          variant: "destructive",
          title: copy.toastPartialFailureTitle,
          description: copy.toastPartialFailureDescription
            .replace("{filename}", stagedItemName(failedItem))
            .replace("{count}", String(committed)),
        });
      }

      if (committed > 0) {
        queryClient.invalidateQueries({ queryKey: ["custom-folders", buildingId, projectId] });

        // Suppressed on a partial failure — that path has already reported both
        // what landed and what didn't, and a success toast would contradict it.
        if (!failedItem) {
          const only = committed === 1 ? items[0] : null;
          if (only?.kind === "upload") {
            toast({
              title: copy.toastSuccessTitle,
              description: copy.toastSuccessDescription
                .replace("{filename}", only.file.name)
                .replace("{version}", String(firstVersion)),
            });
          } else if (only?.kind === "existing") {
            const description =
              only.mode === "copy"
                ? copy.toastCopiedDescription
                : firstVersion === lastVersion
                  ? copy.toastMovedSingleDescription
                  : copy.toastMovedDescription;
            toast({
              title: copy.toastSuccessTitle,
              description: description
                .replace("{filename}", only.document.filename)
                .replace("{version}", String(firstVersion))
                .replace("{first}", String(firstVersion))
                .replace("{last}", String(lastVersion)),
            });
          } else {
            toast({
              title: copy.toastBatchSuccessTitle,
              description: copy.toastBatchSuccessDescription
                .replace("{count}", String(liveAdded))
                .replace("{first}", String(firstVersion))
                .replace("{last}", String(lastVersion)),
            });
          }
        }

        // Side effects the user did not directly ask for are said out loud,
        // whether or not the whole run succeeded.
        for (const rename of results.flatMap((r) => r.renamed)) {
          toast({
            title: copy.toastRenamedTitle,
            description: copy.toastRenamedDescription
              .replace("{from}", rename.from)
              .replace("{to}", rename.to),
          });
        }
        const detached = results.some((r) => r.issueRootAction === "detached");
        const anchorsRemoved = results.reduce((sum, r) => sum + r.anchorsRemoved, 0);
        if (detached || anchorsRemoved > 0) {
          toast({
            title: copy.toastCrossProjectTitle,
            description: [
              detached ? copy.toastDetachedDescription : null,
              anchorsRemoved > 0
                ? copy.toastAnchorsRemovedDescription.replace("{count}", String(anchorsRemoved))
                : null,
            ]
              .filter(Boolean)
              .join(" "),
          });
        }

        if (onUploadComplete) onUploadComplete();
      }

      const touched = new Set<string>();
      if (targetDocument.version_group) touched.add(targetDocument.version_group);
      for (const result of results) {
        touched.add(result.targetVersionGroup);
        if (result.sourceVersionGroup) touched.add(result.sourceVersionGroup);
      }

      return { committed, firstVersion, lastVersion, touchedVersionGroups: [...touched] };
    },
    [uploadAsNewVersion, queryClient, buildingId, projectId, toast, t, onUploadComplete]
  );

  // Get the upload queue store for bulk uploads
  const uploadQueueStore = useUploadQueueStore();

  // Track which files we've already created document records for
  const processedFileIds = useRef<Set<string>>(new Set());

  // Track files currently being processed (for race condition prevention)
  // This is checked synchronously before starting async createDocumentRecord
  const processingFileIds = useRef<Set<string>>(new Set());

  // Track files whose document-record creation has hard-failed for this
  // session. Without this, the reaction below would re-fire every time the
  // queue mutates (status / progress updates) and re-attempt
  // createDocumentRecord, producing a toast/log per attempt for the same
  // file. A document-creation failure is a real bug — single-shot only.
  const failedDocumentRecordFileIds = useRef<Set<string>>(new Set());

  /**
   * Create a document record in the database for a completed upload.
   * This is called after a file is successfully uploaded to storage.
   */
  const createDocumentRecord = useCallback(
    async (
      queuedFile: QueuedFileInstance,
      effectiveProjectId?: string,
      onCommitPossibilityChange?: (mayHaveCommitted: boolean) => void
    ) => {
      // Use per-file config to prevent race conditions when navigating between buildings
      // Each queued file stores its own buildingId and orgId from when it was added
      const fileBuildingId = queuedFile.buildingId;
      const fileOrgId = queuedFile.orgId;
      let registrationMayHaveCommitted = false;
      const setCommitPossibility = (mayHaveCommitted: boolean) => {
        registrationMayHaveCommitted = mayHaveCommitted;
        onCommitPossibilityChange?.(mayHaveCommitted);
      };

      try {
        if (!userProfile?.id || !fileOrgId) {
          throw new Error("Cannot create document record: missing user or organisation");
        }
        const numericBuildingId = parseBuildingStorageId(fileBuildingId);
        if (numericBuildingId === null) {
          throw new Error(`Invalid building ID: ${fileBuildingId}`);
        }

        // Determine which project to use - project selection is required
        let targetProjectId: number | null = null;
        if (effectiveProjectId) {
          targetProjectId = parseInt(effectiveProjectId, 10);
        } else if (projectId) {
          targetProjectId = parseInt(projectId, 10);
        } else {
          throw new Error("Project selection is required for document upload");
        }

        // Determine target folder:
        // 1. If parentFolderId is set, use it as the base folder
        // 2. If relativePath has nested folders, create them under the parent folder
        // 3. If no nested folders in relativePath, use parentFolderId directly
        let targetFolderId: number | null = null;
        const relativePath = queuedFile.relativePath;
        const parentFolderIdFromQueue = queuedFile.parentFolderId;

        // Start with parentFolderId if provided (user selected a folder before uploading)
        if (parentFolderIdFromQueue) {
          targetFolderId = parseInt(parentFolderIdFromQueue, 10);
          if (isNaN(targetFolderId)) {
            console.warn("[useDocumentUpload] Invalid parentFolderId:", parentFolderIdFromQueue);
            targetFolderId = null;
          }
        }

        // Validate relativePath to prevent path traversal attacks
        const validatedPath = validateRelativePath(relativePath);

        // If relativePath has folder structure, create hierarchy under the parent folder
        if (validatedPath && validatedPath.includes("/")) {
          const folderNames = validatedPath.split("/").slice(0, -1);
          console.log("[useDocumentUpload] Creating folder hierarchy for queued file:", {
            relativePath,
            validatedPath,
            folderNames,
            parentFolderId: parentFolderIdFromQueue,
          });

          try {
            // Pass parentFolderId as the starting point for folder creation
            targetFolderId = await createFolderHierarchyWithParent({
              supabase,
              orgId: fileOrgId,
              buildingId: numericBuildingId,
              projectId: targetProjectId,
              folderNames,
              userId: userProfile.id,
              parentFolderId: targetFolderId,
            });
          } catch (folderError) {
            console.error("Folder creation error:", folderError);
            throw new Error(
              `Failed to create folder structure: ${folderError instanceof Error ? folderError.message : "Unknown error"}`
            );
          }
        }

        // Detect special file types (shared helper, WHA-3689).
        const containerType = getContainerType(queuedFile.fileName, queuedFile.contentType);

        // Ensure fresh auth token before RPC call (token may have expired during batch upload)
        try {
          await ensureFreshToken(false);
        } catch {
          throw new Error("Session expired. Please sign in again.");
        }

        // Use versioning RPC for atomic document creation with duplicate detection
        let rpcResult;
        let rpcError;
        setCommitPossibility(true);
        ({ data: rpcResult, error: rpcError } = await supabase.rpc(
          "create_document_with_versioning",
          {
            p_org_id: fileOrgId,
            p_building_id: numericBuildingId,
            p_project_id: targetProjectId,
            p_folder_id: targetFolderId,
            p_filename: queuedFile.fileName,
            p_file_path: queuedFile.filePath,
            p_content_type: queuedFile.contentType,
            p_size: queuedFile.fileSize,
            p_content_hash: queuedFile.contentHash,
            p_created_by: userProfile.id,
            p_source: "bulk_upload",
            p_parent_document_id: null,
            p_container_type: containerType,
          }
        ));

        // If RPC fails with auth error, refresh token and retry once
        if (rpcError) {
          const errMsg = rpcError.message || "";
          const rejected = isTransactionalRpcRejection(rpcError);
          if (rejected) setCommitPossibility(false);
          if (
            rejected &&
            (errMsg.includes("JWT") || errMsg.includes("401") || errMsg.includes("Unauthorized"))
          ) {
            console.warn(
              "[useDocumentUpload] Auth error on RPC, force-refreshing token and retrying"
            );
            try {
              await ensureFreshToken(true);
            } catch {
              throw new Error("Session expired. Please sign in again.");
            }

            setCommitPossibility(true);
            const retryResult = await supabase.rpc("create_document_with_versioning", {
              p_org_id: fileOrgId,
              p_building_id: numericBuildingId,
              p_project_id: targetProjectId,
              p_folder_id: targetFolderId,
              p_filename: queuedFile.fileName,
              p_file_path: queuedFile.filePath,
              p_content_type: queuedFile.contentType,
              p_size: queuedFile.fileSize,
              p_content_hash: queuedFile.contentHash,
              p_created_by: userProfile.id,
              p_source: "bulk_upload",
              p_parent_document_id: null,
              p_container_type: containerType,
            });

            if (retryResult.error) {
              if (isTransactionalRpcRejection(retryResult.error)) {
                setCommitPossibility(false);
              }
              console.error("Document versioning RPC retry error:", retryResult.error);
              throw new Error(
                storageQuotaErrorMessage(retryResult.error.code) ??
                  storageQuotaErrorMessage(retryResult.error.message) ??
                  `Document creation failed: ${retryResult.error.message}`
              );
            }
            rpcResult = retryResult.data;
            rpcError = null;
          } else {
            console.error("Document versioning RPC error:", rpcError);
            throw new Error(
              storageQuotaErrorMessage(rpcError.code) ??
                storageQuotaErrorMessage(rpcError.message) ??
                `Document creation failed: ${rpcError.message}`
            );
          }
        }

        // Validate RPC response structure at runtime
        if (!isVersioningRpcResponse(rpcResult)) {
          console.error("Invalid RPC response format:", rpcResult);
          throw new Error("Document creation failed: Invalid response format from server");
        }

        const versionResult = rpcResult;
        setCommitPossibility(false);

        if (versionResult.action === "skipped" || versionResult.action === "resurrected") {
          const completedReservationId = queuedFile.reservationId;
          if (completedReservationId) {
            await discardBuildingStorageUpload(
              queuedFile.filePath,
              completedReservationId,
              numericBuildingId
            );
            queuedFile.clearReservation();
          }
        } else {
          queuedFile.clearReservation();
        }
        invalidateBuildingStorageQuota(fileBuildingId);

        track("document_uploaded", {
          file_ext: fileExtProp(queuedFile.fileName),
          size_bucket: sizeBucket(queuedFile.fileSize),
          action: versionResult.action,
          source: analyticsSource,
        });

        // Handle skipped duplicates using the existing document row.
        if (versionResult.action === "skipped") {
          return {
            id: versionResult.document_id,
            version: versionResult.version,
            version_group: versionResult.version_group,
          };
        }

        // Handle resurrected documents - soft-deleted doc with same content was restored
        if (versionResult.action === "resurrected") {
          return {
            id: versionResult.document_id,
            version: versionResult.version,
            version_group: versionResult.version_group,
          };
        }

        // Second copy in the write-once backups bucket, now that a row owns the
        // uploaded object — the queue's TUS upload doesn't mirror, precisely so
        // a file the RPC rejects (or the size check fails) never gets a backup
        // nothing can delete. The `skipped` and `resurrected` returns above both
        // leave this object unowned (a resurrected row keeps its own old
        // file_path), so they get no backup either.
        await mirrorToBackups(queuedFile.filePath);

        // Build post-RPC updates
        const postRpcUpdates: Record<string, unknown> = {};

        // For ZIP files, set state to CONTAINER (RPC defaults to UNPROCESSED)
        // DWG files: RPC sets DWG_CONTAINER via container_type parameter — no override needed
        if (containerType === "zip") {
          postRpcUpdates.state = "CONTAINER";
        }

        // Set dates from embedded file metadata (extracted at queue time)
        if (queuedFile.embeddedCreatedAt) {
          postRpcUpdates.original_created_at = queuedFile.embeddedCreatedAt;
        }
        if (queuedFile.embeddedModifiedAt) {
          postRpcUpdates.document_content_modified_date = queuedFile.embeddedModifiedAt;
        }

        // Apply post-RPC updates if any
        if (Object.keys(postRpcUpdates).length > 0) {
          const { error: updateError } = await supabase
            .from("documents")
            .update(postRpcUpdates)
            .eq("id", versionResult.document_id);

          if (updateError) {
            console.error("[useDocumentUpload] Post-RPC update failed:", updateError);
          }
        }

        // Create docData object for downstream compatibility
        const docData = {
          id: versionResult.document_id,
          version: versionResult.version,
          version_group: versionResult.version_group,
        };

        // Invalidate queries
        queryClient.invalidateQueries({ queryKey: ["custom-folders", buildingId, projectId] });

        return docData;
      } catch (error) {
        if (registrationMayHaveCommitted) {
          queuedFile.setDocumentCreationUncertain(UNCONFIRMED_REGISTRATION_MESSAGE);
          capture(error instanceof Error ? error : new Error(String(error)), {
            tags: { feature: "building-storage-quota", stage: "registration-unconfirmed" },
            fingerprint: ["building-storage-quota", "registration-unconfirmed"],
            context: {
              quota: {
                buildingId: fileBuildingId,
                fileId: queuedFile.id,
                path: queuedFile.filePath,
              },
            },
          });
        }
        console.error("Error creating document record:", error);
        throw error;
      }
    },
    [buildingId, projectId, userProfile, queryClient, analyticsSource]
  );

  /**
   * Handle bulk file upload using the queue system.
   * Files are validated, added to the queue, and processed with concurrency control.
   * Document records are created after each successful upload.
   *
   * @param files - Array of files to upload
   * @param relativePaths - Optional array of relative paths (for folder structure)
   * @param projectIdOverride - Optional project ID to use instead of the hook's default
   * @param parentFolderId - Optional parent folder ID for uploads into a selected folder
   * @param directoryPaths - Every directory in the selection, empty ones included
   *   (see `UploadSelection`); each is created even when no file lands in it
   */
  const handleBulkUpload = useCallback(
    async (
      files: File[],
      relativePaths?: string[],
      projectIdOverride?: string,
      parentFolderId?: string,
      directoryPaths: string[] = []
    ) => {
      if (!files.length && !directoryPaths.length) return;

      const numericBuildingId = parseBuildingStorageId(buildingId);
      if (numericBuildingId === null) {
        toast({
          variant: "destructive",
          title: t.common.error,
          description: `Invalid building ID: ${buildingId}`,
        });
        return;
      }

      if (!userProfile?.id) {
        toast({
          variant: "destructive",
          title: t.common.error,
          description: "User profile not available. Please ensure you are logged in.",
        });
        return;
      }

      if (!currentOrganization?.id) {
        toast({
          variant: "destructive",
          title: t.common.error,
          description: "No organisation selected. Please select an organisation before uploading.",
        });
        return;
      }

      const currentQuota = queryClient.getQueryData<BuildingStorageQuota>(
        buildingStorageQuotaKey(buildingId, currentOrganization.id)
      );
      if (currentQuota && uploadsBlocked(currentQuota)) {
        reportStorageAdmissionFailure(buildingId, currentOrganization.id);
        return;
      }
      const effectiveProjectId = projectIdOverride ?? projectId;

      // Check if project is required
      if (requireProject && !effectiveProjectId) {
        toast({
          variant: "destructive",
          title: "Project Required",
          description: "Please select a project before uploading files.",
        });
        return;
      }

      // Attach relative paths to files using a custom property
      // This allows the queue to preserve folder structure
      let filesWithPaths = files.map((file, index) => {
        const fileWithPath = file as File & { webkitRelativePath?: string };
        if (relativePaths && relativePaths[index]) {
          // Create a new File-like object with the webkitRelativePath set
          Object.defineProperty(fileWithPath, "webkitRelativePath", {
            value: relativePaths[index],
            writable: false,
          });
        }
        return fileWithPath;
      });

      // Pre-flight: detect filename collisions for flat files (those not
      // under a new sub-folder) and prompt the user per pair. Files inside
      // nested sub-folders skip the check — those sub-folders don't exist
      // yet, so no collision is possible.
      let finalFiles: Array<File & { webkitRelativePath?: string }> = filesWithPaths;
      // Byte-identical duplicates the pre-flight decides on its own. Held, not reported yet:
      // the batch they belong to can still be withdrawn in the collision dialog, and a
      // withdrawn batch has no outcomes at all. Reported once the batch is committed — which
      // includes the batch that is ENTIRELY duplicates and so has nothing left to queue: the
      // user committed it and the system decided to skip, the commonest duplicate case there
      // is. Only the dialog's cancel returns without calling this.
      let skippedDuplicates: File[] = [];
      const reportSkippedDuplicates = () => {
        for (const dup of skippedDuplicates) {
          track("document_uploaded", {
            file_ext: fileExtProp(dup.name),
            size_bucket: sizeBucket(dup.size),
            action: "skipped",
            source: analyticsSource,
          });
        }
      };
      const numProjectId = effectiveProjectId ? parseInt(effectiveProjectId, 10) : NaN;
      const numFolderId = parentFolderId ? parseInt(parentFolderId, 10) : null;
      if (!isNaN(numProjectId) && (numFolderId === null || !isNaN(numFolderId))) {
        const flatEntries = filesWithPaths.filter((f) => {
          const path = f.webkitRelativePath ?? "";
          return !path.includes("/");
        });

        if (flatEntries.length > 0) {
          try {
            const collisions = await checkUploadCollisions({
              projectId: numProjectId,
              folderId: numFolderId,
              filenames: flatEntries.map((f) => f.name),
            });

            if (collisions.length > 0) {
              const collisionByFilename = new Map(collisions.map((c) => [c.filename, c]));

              // Byte-content check: for each filename collision, compute the
              // incoming file's SHA256 and compare to the existing row's
              // content_hash. Files with identical bytes are silently skipped
              // — they'd hit the RPC's 'skipped' path anyway, and surfacing a
              // "New Version" option that then silently no-ops is confusing.
              // Mismatched-content (or any side's hash missing) falls through
              // to the dialog so the user picks explicitly.
              const contentDuplicates = new Set<File>();
              const collisionEntries = flatEntries.filter((f) => collisionByFilename.has(f.name));
              const hashes = await Promise.all(
                collisionEntries.map(async (f) => {
                  try {
                    return await computeFileHash(f);
                  } catch (hashErr) {
                    console.warn(
                      "[useDocumentUpload] Hash failed during pre-flight, falling back to dialog:",
                      f.name,
                      hashErr
                    );
                    return null;
                  }
                })
              );
              collisionEntries.forEach((file, i) => {
                const hit = collisionByFilename.get(file.name);
                const incomingHash = hashes[i];
                if (
                  hit &&
                  incomingHash &&
                  hit.existing_content_hash &&
                  incomingHash === hit.existing_content_hash
                ) {
                  contentDuplicates.add(file);
                }
              });

              const pairs: CollisionPair[] = [];
              const pairIdToFile = new Map<string, File & { webkitRelativePath?: string }>();
              flatEntries.forEach((file, idx) => {
                const hit = collisionByFilename.get(file.name);
                if (!hit) return;
                if (contentDuplicates.has(file)) return; // byte-identical — silent skip
                const id = `${idx}-${file.name}`;
                pairs.push({
                  id,
                  incomingFile: file,
                  existingFilename: hit.filename,
                  existingVersion: hit.current_version,
                  versionGroup: hit.version_group,
                  existingSize: hit.existing_size,
                  existingModifiedAt: hit.existing_modified_at,
                  existingUploaderName: hit.existing_uploader_name,
                });
                pairIdToFile.set(id, file);
              });

              // Strip byte-identical duplicates from the batch immediately —
              // don't waste a storage upload on something the RPC would skip.
              if (contentDuplicates.size > 0) {
                // Same outcome the RPC would have reported, just decided earlier — so it is
                // reported here too, or these files leave the funnel with no outcome at all
                // and the skipped rate reads low. Deferred to the commit point below.
                skippedDuplicates = Array.from(contentDuplicates);
                filesWithPaths = filesWithPaths.filter((f) => !contentDuplicates.has(f));
                finalFiles = filesWithPaths;
                toast({
                  title: t.documents.versioning.toasts.identicalSkippedTitle,
                  description: t.documents.versioning.toasts.identicalSkippedDescription.replace(
                    "{count}",
                    String(contentDuplicates.size)
                  ),
                });
              }

              if (pairs.length > 0) {
                const decisions = await new Promise<Record<string, CollisionDecision> | null>(
                  (resolvePromise) => {
                    setCollisionDialog({
                      pairs,
                      resolve: (d) => {
                        setCollisionDialog(null);
                        resolvePromise(d);
                      },
                      cancel: () => {
                        setCollisionDialog(null);
                        resolvePromise(null);
                      },
                    });
                  }
                );

                if (decisions === null) {
                  // User aborted the whole batch.
                  return;
                }

                const skipFiles = new Set<File>();
                const renames = new Map<File, string>();
                for (const [id, decision] of Object.entries(decisions)) {
                  const file = pairIdToFile.get(id);
                  if (!file) continue;
                  if (decision === "skip") {
                    skipFiles.add(file);
                  } else if (decision === "duplicate") {
                    // Placeholder — resolved in the batch suffix pass below.
                    renames.set(file, file.name);
                  }
                  // "version" → no rename needed; RPC handles identity match.
                }

                if (renames.size > 0) {
                  const existingInFolder = await fetchFilenamesInFolder({
                    projectId: numProjectId,
                    folderId: numFolderId,
                  });
                  // Track names claimed inside this batch so two duplicates
                  // of the same base filename don't pick the same (N).
                  const claimed = new Set<string>(existingInFolder);
                  for (const file of renames.keys()) {
                    const suffixed = nextDuplicateSuffix(file.name, Array.from(claimed));
                    renames.set(file, suffixed);
                    claimed.add(suffixed);
                  }
                }

                let skippedCount = 0;
                const rebuilt: Array<File & { webkitRelativePath?: string }> = [];
                for (const file of filesWithPaths) {
                  if (skipFiles.has(file)) {
                    skippedCount += 1;
                    continue;
                  }
                  const rename = renames.get(file);
                  if (rename && rename !== file.name) {
                    const preservedPath = file.webkitRelativePath;
                    const renamed = new File([file], rename, {
                      type: file.type,
                      lastModified: file.lastModified,
                    }) as File & { webkitRelativePath?: string };
                    if (preservedPath) {
                      Object.defineProperty(renamed, "webkitRelativePath", {
                        value: preservedPath,
                        writable: false,
                      });
                    }
                    rebuilt.push(renamed);
                  } else {
                    rebuilt.push(file);
                  }
                }
                finalFiles = rebuilt;

                if (skippedCount > 0) {
                  toast({
                    title: t.documents.versioning.toasts.filesSkippedTitle,
                    description: t.documents.versioning.toasts.filesSkippedDescription.replace(
                      "{count}",
                      String(skippedCount)
                    ),
                  });
                }
              }
            }

            // Nothing left to upload — every colliding file was a byte-identical duplicate,
            // or the user skipped the rest in the dialog. Skip the queue altogether (the
            // toast above already told the user), but report the skips the SYSTEM decided
            // first: the batch was committed, they are its outcome. Folders the batch
            // brought still have to be created, so those carry on below.
            if (finalFiles.length === 0 && directoryPaths.length === 0) {
              reportSkippedDuplicates();
              return;
            }
          } catch (err) {
            console.error("[useDocumentUpload] Collision pre-flight failed:", err);
            // Fall through to normal upload on pre-flight failure.
          }
        }
      }

      // The batch is committed from here on, so the skips the pre-flight decided are real
      // outcomes now. Only a batch the user ABORTED in the collision dialog returns above
      // without reporting — the same rule the RPC-side emit states for a file the USER chose
      // to skip: a withdrawn upload is not an outcome.
      reportSkippedDuplicates();

      // The whole folder tree goes up before the first file does. From the paths
      // of the files the user actually submitted (not the ones skipped above),
      // plus every directory the selection named.
      const folderPaths = collectFolderPaths(
        finalFiles.map((f) => f.webkitRelativePath ?? ""),
        directoryPaths
      );
      if (folderPaths.length > 0) {
        try {
          if (isNaN(numProjectId)) throw new Error("Project selection is required for upload");
          await createUploadFolderTree({
            supabase,
            orgId: currentOrganization.id,
            buildingId: numericBuildingId,
            projectId: numProjectId,
            userId: userProfile.id,
            parentFolderId: numFolderId !== null && !isNaN(numFolderId) ? numFolderId : null,
            folderPaths,
          });
        } catch (folderError) {
          // Queueing the files anyway would fail every one of them on the same
          // folder, one record at a time — stop here and say why, once.
          console.error("[useDocumentUpload] Folder tree creation failed:", folderError);
          toast({
            variant: "destructive",
            title: t.documents.upload.folderCreationFailedTitle,
            description: t.documents.upload.folderCreationFailedDescription.replace(
              "{reason}",
              folderError instanceof Error ? folderError.message : String(folderError)
            ),
          });
          return;
        } finally {
          // Folders created before a failure are real too.
          void queryClient.invalidateQueries({ queryKey: ["custom-folders", buildingId] });
        }

        if (finalFiles.length === 0) {
          // No files, so no progress panel — this toast is the only sign it worked.
          toast({
            title: t.documents.upload.foldersUploaded.replace(
              "{count}",
              String(folderPaths.length)
            ),
          });
          return;
        }
      }

      // Add files to the queue
      let result: Awaited<ReturnType<typeof uploadQueueStore.addFiles>>;
      try {
        result = await uploadQueueStore.addFiles(finalFiles, {
          buildingId,
          orgId: currentOrganization.id,
          projectId: effectiveProjectId,
          parentFolderId,
        });
      } catch (error) {
        if (error instanceof BuildingStorageQuotaError) {
          reportStorageAdmissionFailure(buildingId, currentOrganization.id);
        } else
          toast({
            variant: "destructive",
            title: t.common.error,
            description: error instanceof Error ? error.message : t.documents.upload.uploadError,
          });
        return;
      }

      // Show toast for skipped files (empty/unreadable only — unsupported types go through FailedUploadsPanel)
      if (result.skipped > 0) {
        const parts: string[] = [];
        if (result.skippedByReason.empty > 0) parts.push(`${result.skippedByReason.empty} empty`);
        if (result.skippedByReason.too_large > 0)
          parts.push(`${result.skippedByReason.too_large} exceeding 500MB limit`);
        if (result.skippedByReason.unreadable > 0)
          parts.push(`${result.skippedByReason.unreadable} unreadable`);

        if (parts.length > 0) {
          toast({
            variant: "destructive",
            title: "Some files skipped",
            description: `${result.skipped} file(s) were invalid and skipped (${parts.join(", ")})`,
          });
        }
      }

      if (result.added > 0) {
        // Start processing the queue - progress UI will show status
        uploadQueueStore.startProcessing();
      }
    },
    [
      buildingId,
      projectId,
      requireProject,
      userProfile,
      currentOrganization,
      uploadQueueStore,
      queryClient,
      toast,
      t,
      analyticsSource,
    ]
  );

  // Watch for completed uploads and create document records
  useEffect(() => {
    /**
     * Process a single completed file - creates document record.
     * Uses per-file config to prevent race conditions when files from
     * different projects are uploaded simultaneously.
     */
    const processCompletedFile = async (fileId: string) => {
      // Synchronous check to prevent race condition:
      // Multiple files completing simultaneously would all pass the
      // processedFileIds check before any async operation completes.
      // processingFileIds tracks files currently being processed.
      if (processingFileIds.current.has(fileId)) {
        return; // Already being processed
      }
      if (processedFileIds.current.has(fileId)) {
        return; // Already completed
      }
      // Single-shot: if document-record creation already failed once for this
      // file in this session, don't retry. The MobX reaction below re-fires
      // on every queue mutation; without this gate, a failure path would
      // re-enter processCompletedFile and re-attempt indefinitely.
      if (failedDocumentRecordFileIds.current.has(fileId)) {
        return;
      }

      // Mark as processing SYNCHRONOUSLY before any async work
      processingFileIds.current.add(fileId);

      const queuedFile = uploadQueueStore.queue.find((f) => f.id === fileId);
      if (!queuedFile) {
        processingFileIds.current.delete(fileId);
        return;
      }

      // Both confirmed rows and in-flight/uncertain registrations persist across remounts.
      if (queuedFile.documentCreated) {
        processedFileIds.current.add(fileId);
        processingFileIds.current.delete(fileId);
        return;
      }
      if (queuedFile.registrationUncertain) {
        processingFileIds.current.delete(fileId);
        return;
      }

      let registrationMayHaveCommitted = false;
      try {
        // Persist the guard before any async preparation or RPC can outlive this hook.
        queuedFile.markRegistrationInFlight();
        // Use per-file config instead of global currentConfig
        // This prevents race conditions when files from different projects
        // are in the queue simultaneously
        const documentRecord = await createDocumentRecord(
          queuedFile,
          queuedFile.projectId ?? undefined,
          (mayHaveCommitted) => {
            registrationMayHaveCommitted = mayHaveCommitted;
          }
        );
        if (!documentRecord) throw new Error("Document creation did not confirm a document record");

        // Mark as processed AFTER successful document creation
        processedFileIds.current.add(fileId);
        queuedFile.setDocumentCreated();

        console.log("[useDocumentUpload] Document record created for:", queuedFile.fileName);
      } catch (error) {
        // Document-record creation failure is a real bug, not a user-facing
        // condition. Surface it loudly to dev/Sentry and mark the file as
        // hard-failed so the reaction stops re-attempting; do NOT toast at
        // the user — they have no way to act on this.
        failedDocumentRecordFileIds.current.add(fileId);
        const errorMessage = error instanceof Error ? error.message : "Document creation failed";
        const quotaMessage = storageQuotaErrorMessage(errorMessage);
        if (queuedFile.reservationId && !registrationMayHaveCommitted) {
          try {
            const numericBuildingId = parseBuildingStorageId(queuedFile.buildingId);
            if (numericBuildingId === null) {
              throw new Error(`Invalid building ID: ${queuedFile.buildingId}`);
            }
            await discardBuildingStorageUpload(
              queuedFile.filePath,
              queuedFile.reservationId,
              numericBuildingId
            );
          } catch (cancelError) {
            capture(cancelError instanceof Error ? cancelError : new Error(String(cancelError)), {
              tags: { feature: "building-storage-quota", stage: "discard-upload" },
              fingerprint: ["building-storage-quota", "discard-upload"],
              context: { quota: { buildingId: queuedFile.buildingId, fileId } },
            });
          }
        }
        if (!registrationMayHaveCommitted) {
          queuedFile.setDocumentCreationFailed(quotaMessage ?? errorMessage);
        }

        // ONE exception, and it is the opposite case: a billing write guard
        // refused the row on purpose, and the user is the only one who can lift
        // it. Staying silent here means the file uploads, the row is refused,
        // and the document simply never appears.
        const billingRefusal = billingGuardMessage(error, t);
        if (billingRefusal) {
          toast({
            variant: "destructive",
            title: t.common.error,
            description: billingRefusal,
          });
          // `finally` below releases the processing lock.
          return;
        }

        const context = {
          fileId,
          fileName: queuedFile.fileName,
          fileSize: queuedFile.fileSize,
          contentType: queuedFile.contentType,
          filePath: queuedFile.filePath,
          orgId: queuedFile.orgId,
          buildingId: queuedFile.buildingId,
          projectId: queuedFile.projectId,
          parentFolderId: queuedFile.parentFolderId,
          relativePath: queuedFile.relativePath,
          userId: userProfile?.id,
        };
        console.error(
          "[useDocumentUpload] Document record creation failed — investigate, this is a bug:",
          context,
          error
        );
        Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
          tags: { feature: "document-upload", stage: "create-document-record" },
          extra: context,
        });
      } finally {
        // Always clean up processing state
        processingFileIds.current.delete(fileId);
        // A user may have dismissed the row while its document RPC was in
        // flight. All logging/context above has read the live MST node now.
        uploadQueueStore.finalizeDismissed(fileId);
      }
    };

    const disposer = reaction(
      // Track the queue items and their statuses
      () =>
        uploadQueueStore.queue.map((f) => ({
          id: f.id,
          status: f.status,
          filePath: f.filePath,
          registrationUncertain: f.registrationUncertain,
        })),
      async (queueItems) => {
        // Find newly completed files that we haven't processed yet
        for (const item of queueItems) {
          if (
            item.status === "complete" &&
            !item.registrationUncertain &&
            !processedFileIds.current.has(item.id)
          ) {
            // Process each file - race condition prevention is inside processCompletedFile
            void processCompletedFile(item.id);
          }
        }
      },
      // fireImmediately: true to catch files that completed while navigated away
      { fireImmediately: true }
    );

    return () => {
      disposer();
    };
  }, [uploadQueueStore, createDocumentRecord, toast, t, userProfile?.id]);

  // Watch for queue completion to trigger refresh
  useEffect(() => {
    const disposer = reaction(
      () => uploadQueueStore.isComplete,
      (isComplete) => {
        if (isComplete && uploadQueueStore.completedCount > 0) {
          // NOTE: Do NOT clear processedFileIds here!
          // If user adds more files without resetting, completed files still exist in queue.
          // Clearing processedFileIds would cause duplicate document record creation
          // for files already in the queue with status "complete".
          // processedFileIds is only cleared when:
          // - Component unmounts (ref is garbage collected)
          // - Queue is explicitly reset via uploadQueueStore.reset()

          // Trigger completion callback if provided
          if (onUploadComplete) {
            onUploadComplete();
          }

          // Progress UI shows completion status - no toast needed
        }
      },
      { fireImmediately: false }
    );

    return () => {
      disposer();
    };
  }, [uploadQueueStore, onUploadComplete]);

  return {
    isUploading,
    handleFileUpload,
    handleBulkUpload,
    uploadAsNewVersion,
    addVersionsInOrder,
    fileProgresses,
    uploadQueueStore,
    collisionDialog,
  };
}
